import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { buildDuelPool } from '../../domain/game/carDuel';
import { duelInsight } from '../../domain/game/duelInsight';
import {
    advanceRun,
    answerRun,
    claimRun,
    expireRun,
    restartRun,
    startRun,
} from '../../domain/game/duelRun';
import { useLocale } from '../i18n/LocaleProvider';
import { bestScore, bestStreak, readLeaderboard, recordRun } from './leaderboard';

/**
 * A run of duels, bound to the catalogue on screen (SPEC-007 PR-7.5, PR-7.6).
 *
 * Everything about WHAT happens is `domain/game`; this hook is the four things
 * that cannot be pure — when the pool is rebuilt, where the device's board is
 * kept, which draw of `Math.random` each deal gets, and the clock.
 *
 * The pool is memoised on the catalogue, not on the run. Building it is
 * hundreds of comparisons over the reader's own cars, which is nothing once and
 * is nothing worth
 * doing on every tap; and rebuilding it mid-run would deal from a different set
 * of questions than the one `seen` was written against.
 *
 * `random` is never passed from here. The domain takes it as a defaulted
 * parameter resolved at CALL time, so a suite pinning `Math.random` pins the
 * deal — which is what makes the screen's own tests deterministic without the
 * production path carrying a seam it does not use.
 */
export default function useDuelRun({ cars }) {
    const { languageCode } = useLocale();
    const pool = useMemo(() => buildDuelPool(cars), [cars]);
    const [run, setRun] = useState(null);
    const [standing, setStanding] = useState(() => ({ board: readLeaderboard(), rank: null }));

    /**
     * Opened once, on the first pool that can be played.
     *
     * A catalogue that arrives after the first render — which is the normal
     * case, because it is fetched — starts the run then. What the guard inside
     * the updater buys is everything after that: the pool CHANGES under a live
     * run for two reasons now, and neither of them is a reason to throw the run
     * away. A language switch rebuilds it from a freshly joined catalogue that
     * is the same cars with different prose beside them; and winning a car
     * (SPEC-010) adds one to it while the ceremony for that car is on screen.
     *
     * Without the guard the second of those destroys the ceremony a frame after
     * it opens, and the first has been quietly ending runs since the module was
     * written — the comment here has claimed otherwise from the first day.
     *
     * A pool that grows joins the NEXT run, which is what `restart` deals from.
     */
    useEffect(() => {
        setRun((current) => {
            if (current && current.status !== 'unavailable') {
                return current;
            }

            const board = readLeaderboard();

            return pool.length > 0
                ? startRun(pool, { best: bestStreak(board), record: bestScore(board) })
                : startRun([], {});
        });
    }, [pool]);

    const answer = useCallback((carId) => setRun((current) => answerRun(current, carId)), []);

    const next = useCallback(() => setRun((current) => advanceRun(current, pool)), [pool]);

    /**
     * A car taken mid-run: the ladder's bar moves to the score that bought it
     * (SPEC-010 PR-10.11).
     *
     * Pure state, so it belongs in an updater — unlike the two writes in this
     * hook, nothing here touches storage. The CAR is somebody else's business:
     * `useCarPrize` is what decides one is owed and `useCarCollection` is what
     * keeps it, and this callback is the third of the three things that happen
     * in one gesture. All three are set in the same handler, so React batches
     * them into a single render and the ladder is never drawn against a bar the
     * collection has already moved past.
     */
    const claim = useCallback(() => setRun((current) => claimRun(current)), []);

    const restart = useCallback(() => {
        // Both records re-read HERE rather than carried off the ended run,
        // because the run that just ended has since been banked: the streak
        // record moved inside the run and the score record moved when the board
        // took it. Re-reading is one storage hit per new run and it is the only
        // way the next target is quoted against the bar the reader can see.
        const board = readLeaderboard();

        // The rank belongs to the run that earned it, so it goes when that run
        // does — otherwise the next run opens already wearing somebody else's
        // placing.
        setStanding({ board, rank: null });

        setRun((current) => restartRun(current, pool, {
            best: bestStreak(board),
            record: bestScore(board),
        }));
    }, [pool]);

    /**
     * Bank the run when it ends.
     *
     * In an effect rather than inside `answer`, because there are TWO ways a run
     * ends and only one of them is a tap: the clock's own tick calls
     * `expireRun` from inside a state updater, and a write to Web Storage does
     * not belong in one — React may call an updater more than once.
     *
     * `recorded` holds the VERDICT the last recording was made from. It is a
     * fresh object per ending, so it is an identity for "this run, this time"
     * that survives the re-renders between the ending and the restart — where a
     * boolean would need clearing and a score would collide with the next run
     * that happened to end on the same number.
     */
    const recorded = useRef(null);

    useEffect(() => {
        if (run?.status !== 'over' || recorded.current === run.verdict) {
            return;
        }

        recorded.current = run.verdict;
        setStanding(recordRun({ score: run.score, streak: run.streak }));
    }, [run]);

    /**
     * The clock.
     *
     * ONE interval, at one second, and it runs only while a question is open —
     * not through the reveal, not on the empty and failed screens, and not once
     * the run has ended. A timer left running through a reveal is the bug that
     * ends a run the reader has already won, several seconds after they won it.
     *
     * The tick decrements the run's own `seconds` rather than reading a clock,
     * so the counter and the drain bar are two readings of one number and there
     * is nothing for them to disagree about. It expires the round through the
     * domain's own `expireRun`, which refuses a run that is no longer `asking` —
     * so a tick that lands in the same frame as a tap cannot overwrite a verdict
     * the reader earned.
     *
     * Keyed on the ROUND rather than on the run: a new round restarts the
     * interval from a full second, where a dependency on the whole run object
     * would restart it on every tick and the last second would never elapse.
     */
    const isAsking = run?.status === 'asking';
    const round = run?.round;

    useEffect(() => {
        if (!isAsking) {
            return undefined;
        }

        const ticking = setInterval(() => {
            setRun((current) => {
                if (current?.status !== 'asking') {
                    return current;
                }

                const seconds = current.seconds - 1;

                return seconds > 0 ? { ...current, seconds } : expireRun(current);
            });
        }, 1000);

        return () => clearInterval(ticking);
    }, [isAsking, round]);

    const insight = useMemo(
        () => (run?.verdict ? duelInsight(run.verdict, { languageCode }) : null),
        [run?.verdict, languageCode],
    );

    return {
        // Null only for the first render before the effect has opened a run. The
        // screen draws its loading state for it, which is the same thing it draws
        // while the catalogue is still on its way.
        run,
        insight,
        // The device's five best runs, and where the run that just ended landed
        // on them. `rank` is null until one does.
        board: standing.board,
        rank: standing.rank,
        answer,
        claim,
        next,
        restart,
    };
}
